<template>
  <Button
    id="validate-payment-info"
    :label="translate(`${LOCALE_PREFIX}.actions.validate`)"
    class="mr-3"
    :loading="validating"
    :disabled="!isFormValid || !isFormDirty || saving"
    outlined
    severity="secondary"
    @click.stop="validateEditPaymentInfo"
  >
    <template #icon>
      <i class="material-symbols-rounded text-2xl mr-2">check</i>
    </template>
  </Button>
</template>

<script setup lang="ts">
import { useTranslation } from '@centric-os/helpers';
import { SitesLocalePrefix } from '../../../../../../enums';
import { computed, inject } from 'vue';
import {
  formIdSymbol,
  type ApiResponseError,
  type FreedomPay,
  type PaymentConfigValidation,
} from '@centric-os/types';
import { FinancialFormIDs } from '../../../enums';
import { useFormStore } from '@centric-os/stores/form/use-form-store';
import { useStoreAPIAction } from '@centric-os/stores/plugins/api';
import { useFinancialConfigStore, usePaymentInfoStore } from '../../../../../../routes';
import { storeToRefs } from 'pinia';
import { get } from 'lodash-es';
import useGlobalToast from '@centric-os/components/src/core/messages/toast/composables/use-global-toast';

// Set localization
const { translate } = useTranslation();
const LOCALE_PREFIX = SitesLocalePrefix.SITE_PAYMENT_INFO;

// Set payment info store
const paymentInfoStore = usePaymentInfoStore();
const { isPaymentInfoValid } = storeToRefs(paymentInfoStore);
const { loading: validating } = useStoreAPIAction(paymentInfoStore, 'validatePaymentInfo');
const { loading: saving } = useStoreAPIAction(paymentInfoStore, 'savePaymentInfo');

// Set form store
const activeFormId: string = inject(formIdSymbol, FinancialFormIDs.PAYMENT_INFO_FORM);
const formStore = useFormStore();
const form = formStore.getForm(activeFormId);

const fConfigStore = useFinancialConfigStore();
const { isStationInitialSetup } = storeToRefs(fConfigStore);

const fieldPrefix = computed<string>(() => {
  if (isStationInitialSetup.value) {
    return 'paymentConfig.paymentSettings.';
  }
  return '';
});

const isFormValid = computed<boolean>(() => {
  const activeForm = form.value;
  const isTerminalIdValid = activeForm?.isFieldValid(
    // @ts-expect-error FIXME: Argument of type 'string' is not assignable to parameter of type 'never'.
    `${fieldPrefix.value}freedompay.freedompay_terminal_id`,
  );
  const isStoreIdValid = activeForm?.isFieldValid(
    // @ts-expect-error FIXME: Argument of type 'string' is not assignable to parameter of type 'never'.
    `${fieldPrefix.value}freedompay.freedompay_store_id`,
  );
  // @ts-expect-error FIXME: Argument of type 'string' is not assignable to parameter of type 'never'.
  const isTypeValid = activeForm?.isFieldValid(`${fieldPrefix.value}freedompay.type`);
  return isTerminalIdValid && isStoreIdValid && isTypeValid;
});

const isFormDirty = computed<boolean>(() => {
  const activeForm = form.value;
  const isTerminalIdDirty = activeForm?.isFieldDirty(
    // @ts-expect-error FIXME: Argument of type 'string' is not assignable to parameter of type 'never'.
    `${fieldPrefix.value}freedompay.freedompay_terminal_id`,
  );
  const isStoreIdDirty = activeForm?.isFieldDirty(
    // @ts-expect-error FIXME: Argument of type 'string' is not assignable to parameter of type 'never'.
    `${fieldPrefix.value}freedompay.freedompay_store_id`,
  );
  return isTerminalIdDirty || isStoreIdDirty;
});

// Validate edit payment info
const validateEditPaymentInfo = async (): Promise<void> => {
  const freedomPayKey = `${fieldPrefix.value}freedompay`;
  const formValues: Partial<FreedomPay> = get(form.value.values, freedomPayKey);
  const payload: PaymentConfigValidation = {
    terminal_id: formValues?.freedompay_terminal_id,
    store_id: formValues?.freedompay_store_id,
  };

  try {
    await paymentInfoStore.validatePaymentInfo(payload);

    if (isPaymentInfoValid.value) {
      showSuccessToast(translate(`${LOCALE_PREFIX}.actions.paymentConfigValidationSuccess`));
    } else {
      showErrorToast(translate(`${LOCALE_PREFIX}.actions.paymentConfigValidationError`));
    }
  } catch (error: unknown) {
    const err = error as ApiResponseError;
    showErrorToast(
      translate(`${LOCALE_PREFIX}.actions.paymentConfigValidationError`),
      err.message as string,
    );
  }
};

// Set global toast
const { showErrorToast, showSuccessToast } = useGlobalToast();
</script>
